"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
Object.defineProperty(exports, "__esModule", { value: true });
var common_1 = require("@angular/common");
var core_1 = require("@angular/core");
var FOCUSABLE_ELEMENTS_SELECTOR = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
    'textarea:not([disabled])', '[contenteditable]', '[tabindex]:not([tabindex="-1"])'
].join(', ');
var DIRECTION;
(function (DIRECTION) {
    DIRECTION[DIRECTION["BACKWARD"] = 0] = "BACKWARD";
    DIRECTION[DIRECTION["FORWARD"] = 1] = "FORWARD";
})(DIRECTION || (DIRECTION = {}));
/**
 * Class that enforce the browser focus to be trapped inside a DOM element.
 *
 * The implementation is rather simple, the class add a `focusin` listener on the document with capture phase.
 * Any focus event will then be caught, and therefore the class will only allow the one for elements contained inside
 * it's own element.
 *
 * In case the element is not contained, the class will determine which new element has to be focused based on the `tab`
 * navigation direction.
 *
 * Should not be used directly. Use only via {@link NgbFocusTrapFactory}
 */
var NgbFocusTrap = /** @class */ (function () {
    /**
     * @param _element The element around which focus will be trapped inside
     * @param autofocus Initially put the focus on specific element with a `ngbFocustrap` attribute. Will also remenber
     * and restore any previously focused element on destroy.
     * @param _document Document on which `focusin` and `keydown.TAB` events are listened
     * @param _ngZone The zone Angular is running in
     */
    function NgbFocusTrap(_element, autofocus, _document, _ngZone) {
        var _this = this;
        this._element = _element;
        this._document = _document;
        this._ngZone = _ngZone;
        this._direction = DIRECTION.FORWARD;
        this._endOfDocument = null;
        this._enforceFocus = this._enforceFocus.bind(this);
        this._detectDirection = this._detectDirection.bind(this);
        var eod = this._endOfDocument = this._document.createElement('i');
        eod.className = 'ngb-focustrap-eod';
        eod.tabIndex = 0;
        this._document.body.appendChild(eod);
        this._ngZone.runOutsideAngular(function () {
            _this._document.addEventListener('focusin', _this._enforceFocus, true);
            _this._document.addEventListener('keydown', _this._detectDirection);
            _this._removeDocumentListener = function () {
                _this._document.removeEventListener('focusin', _this._enforceFocus, true);
                _this._document.removeEventListener('keydown', _this._detectDirection);
            };
        });
        if (autofocus === true) {
            this._focusInitial();
        }
    }
    Object.defineProperty(NgbFocusTrap.prototype, "focusableElement", {
        /**
         * Guess the next focusable element.
         * Computation is based on specific CSS selector and [tab] navigation direction
         */
        get: function () {
            var list = this._element.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
            return this._direction === DIRECTION.BACKWARD ? list[list.length - 1] : list[0];
        },
        enumerable: true,
        configurable: true
    });
    /** Detect if incoming focus event should be prevented or not */
    NgbFocusTrap.prototype._enforceFocus = function (event) {
        var _this = this;
        var target = event.target;
        if (this._document !== target && this._element !== target && !this._element.contains(target)) {
            this._ngZone.run(function () {
                var element = _this.focusableElement;
                if (element) {
                    element.focus();
                    event.stopPropagation();
                }
            });
        }
    };
    /** Event handler detecting current `tab` navigation direction */
    NgbFocusTrap.prototype._detectDirection = function (event) {
        var shiftKey = event.shiftKey, key = event.key;
        if (key === 'Tab') {
            this._direction = shiftKey ? DIRECTION.BACKWARD : DIRECTION.FORWARD;
        }
    };
    /** Try to set focus on the first found element that has an ngbAutofocus attribute */
    NgbFocusTrap.prototype._focusInitial = function () {
        var element = this._element.querySelector('[ngbAutofocus]');
        if (element) {
            element.focus();
        }
    };
    /**
     * Destroys the focustrap by removing all event listeners set on document.
     *
     * Eventually put the focus back on the previously focused element at the time
     * focustrap has been initialized.
     */
    NgbFocusTrap.prototype.destroy = function () {
        this._removeDocumentListener();
        this._document.body.removeChild(this._endOfDocument);
    };
    return NgbFocusTrap;
}());
exports.NgbFocusTrap = NgbFocusTrap;
/**
 * Factory service to easily create a `NgbFocusTrap` instance on an element
 */
var NgbFocusTrapFactory = /** @class */ (function () {
    function NgbFocusTrapFactory(_document, _ngZone) {
        this._document = _document;
        this._ngZone = _ngZone;
    }
    /**
     * Create an instance of {@link NgbFocusTrap} and return it
     * @param element HTMLElement to trap focus inside
     * @param autofocus Whether the focustrap should automatically move focus into the trapped element upon
     * initialization and return focus to the previous activeElement upon destruction.
     */
    NgbFocusTrapFactory.prototype.create = function (element, autofocus) {
        if (autofocus === void 0) { autofocus = false; }
        return new NgbFocusTrap(element, autofocus, this._document, this._ngZone);
    };
    NgbFocusTrapFactory = __decorate([
        core_1.Injectable(),
        __param(0, core_1.Inject(common_1.DOCUMENT)),
        __metadata("design:paramtypes", [Document, core_1.NgZone])
    ], NgbFocusTrapFactory);
    return NgbFocusTrapFactory;
}());
exports.NgbFocusTrapFactory = NgbFocusTrapFactory;
//# sourceMappingURL=focus-trap.js.map


//////////////////
// WEBPACK FOOTER
// ./temp/util/focus-trap.js
// module id = 41
// module chunks = 0